import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

/**
 * When a layered graphic is the right overlay, offered by list_scene_designs ahead of the
 * template's overlay designs. It sits where the agent picks an overlay so that it competes with the
 * stock callouts and lists directly, as the chart scene's entry does with the stat designs.
 */
export const LAYERED_GRAPHIC_DESIGN_ENTRY = `### Graphic over the speaker — a \`graphic\` subagent on an overlay copied from the base scene
For what no design here can show while the speaker stays on screen: a diagram or comparison
(curves, bars, A versus B, a cause and its effect), figures that relate ("742 here, 704 there: 38
points apart"), a list longer than a design's slots or one that builds point by point, or a
placement the user asks for that no design gives. A design here that holds the content as it is (a
name and title, one figure or phrase, up to three short points) stays the better choice, and
figures that should take over the frame are a chart scene. Load \`${SKILL_NAMES.layeredGraphicsBasics}\`
before adding one.`;

export const layeredGraphicsBasicsSkill: Skill = {
    name: SKILL_NAMES.layeredGraphicsBasics,
    description:
        'A bespoke animated graphic layered over a scene that already shows something — a talking ' +
        'head or avatar, footage, a stock design — designed by a graphic subagent, so what is ' +
        'beneath stays on screen. Load BEFORE adding or changing one.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Layered Graphics

A layered graphic is an animated graphic on a transparent layer above a scene that already shows
something. What is beneath stays on screen; the graphic adds to it. A graphic subagent designs it.
A graphic that should replace everything beneath belongs in a motion graphics scene of its own.

## The build
1. **Give it a span.** Over a talking head or avatar the base scene spans the whole recording, so
   copy it as an overlay over the words the graphic is about: \`${TOOL_NAMES.addVoiceoverScene}\`
   with the base scene's scene_id as its \`scene_design_id\`, listed in the same call as the group's
   other overlays. The copy shows the speaker as the base does. Over a stock design, clear the text
   the graphic replaces with \`${TOOL_NAMES.setText}\` first.
2. **Hand it to the subagent.** \`${TOOL_NAMES.runSubagent}\` with \`type\` \`graphic\` and
   \`scene_ids\` set to that scene's scene_id; several graphics can be designed in parallel, one
   call each in the same message. It sees a still of the scene, where the speaker is and the words
   over it, but not this conversation, so the \`prompt\` gives the idea in a sentence, the exact
   figures, names or steps, which words each part lands on, and anything the user asked for. Leave
   the visual treatment to it.
3. **Check it.** Review the scene the way you review any other. To change it, start a \`graphic\`
   subagent on the same scene again, saying what to change; never edit its HTML yourself.`;
    },
};
